// useNavigationGuard — ask before an in-app navigation while `when` is true.
//
// A shim, NOT react-router's official blocker: `useBlocker` throws outside a data
// router, and this app mounts a declarative <BrowserRouter> whose route list is
// built from redux inside App's body. So this wraps the history navigator's
// push/replace instead, which covers <Link>/<NavLink> clicks and navigate('/x').
//
// It does NOT cover POP — the browser's own back/forward and navigate(-1) (the
// header arrows) never reach the navigator, so they navigate away unguarded.
// Pair this with a `beforeunload` listener for reload/tab-close.

import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

type Navigate = (...args: never[]) => void;

/**
 * @param when   guard only while this is true (restores the navigator otherwise)
 * @param ask    called instead of navigating; invoke its `proceed` to go through
 */
const useNavigationGuard = (when: boolean, ask: (proceed: () => void) => void) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  // Kept in a ref so re-patching isn't needed when the callback identity changes
  // (it usually closes over fresh state on every render).
  const askRef = useRef(ask);
  askRef.current = ask;

  useEffect(() => {
    if (!when) return;

    const target = navigator as unknown as { push: Navigate; replace: Navigate };
    const original = { push: target.push, replace: target.replace };

    const intercept =
      (method: 'push' | 'replace') =>
      (...args: never[]) =>
        // `proceed` calls the ORIGINAL, never the patched method — otherwise
        // confirming would re-enter the prompt forever.
        askRef.current(() => original[method].apply(navigator, args));

    target.push = intercept('push');
    target.replace = intercept('replace');

    return () => {
      target.push = original.push;
      target.replace = original.replace;
    };
  }, [when, navigator]);
};

export default useNavigationGuard;
